import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../ui/SafeIcon'
import SearchBar from '../ui/SearchBar'
import LanguageSelector from '../ui/LanguageSelector'
import { useCartStore } from '../../store/cartStore'
import { useAuthStore } from '../../store/authStore'

const { FiShoppingCart, FiUser, FiMenu, FiMessageSquare, FiPackage, FiHome } = FiIcons

function Header() {
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false)
  const location = useLocation()
  const { t } = useTranslation()
  const cartItems = useCartStore(state => state.items)
  const { user, isAuthenticated } = useAuthStore()
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0)

  const mainNavLinks = [
    { name: t('nav.home'), href: '/', icon: FiHome },
    { name: 'Ready to Ship', href: '/search?shipment_type=ready_to_ship' },
    { name: t('feature.tradeAssurance'), href: '/trade-assurance' },
    { name: t('nav.buyerCentral'), href: '/buyer-central' },
    { name: t('nav.help'), href: '/help' },
    { name: t('nav.seller'), href: '/seller' },
  ]

  const categories = [
    { name: 'Apparel & Accessories', href: '/search?q=fashion', icon: '👕' },
    { name: 'Electronics', href: '/search?q=electronics', icon: '📱' },
    { name: 'Home & Garden', href: '/search?q=home', icon: '🏠' },
    { name: 'Toys & Hobbies', href: '/search?q=toys', icon: '🧸' },
    { name: 'Beauty & Personal Care', href: '/search?q=beauty', icon: '💄' },
    { name: 'Sports & Entertainment', href: '/search?q=sports', icon: '⚽' },
    { name: 'Machinery', href: '/search?q=machinery', icon: '⚙️' },
    { name: 'Packaging & Printing', href: '/search?q=packaging', icon: '📦' }
  ]

  const isActiveLink = (href) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href.split('?')[0])
  }

  return (
    <>
      {/* Top Alert Bar */}
      <div className="bg-blue-600 text-white text-xs py-1 px-4 text-center relative z-[61]">
        <div className="max-w-[1400px] mx-auto">
          <span className="font-medium">🚀 {t('feature.tradeAssurance')}: {t('cart.secureTransaction')}!</span>
        </div>
      </div>

      {/* Top Utility Bar - FIXED: z-[60] > z-50 ensures dropdowns show over header */}
      <div className="relative z-[60] bg-white border-b border-gray-100 text-gray-600 py-2 px-4 text-xs">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="hover:text-[#FF6600] cursor-pointer hidden sm:inline">Sourcing Solutions</span>
            <span className="hover:text-[#FF6600] cursor-pointer hidden sm:inline">Services & Membership</span>
            <span className="hover:text-[#FF6600] cursor-pointer">Help & Community</span>
          </div>
          <div className="flex items-center space-x-4 sm:space-x-6">
            {/* Language Selector */}
            <LanguageSelector />
            
            <span className="text-gray-300">|</span>
            
            {!isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <Link to="/login" className="hover:text-[#FF6600] font-medium">{t('nav.login')}</Link>
                <span className="text-gray-300">/</span>
                <Link to="/register" className="hover:text-[#FF6600] font-medium">{t('nav.register')}</Link>
              </div>
            ) : (
              <Link to="/dashboard" className="flex items-center space-x-1 hover:text-[#FF6600] font-medium">
                <SafeIcon icon={FiUser} className="w-3 h-3" />
                <span>{user?.name || t('nav.account')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Header - sticky z-50 (lower than Top Bar z-60) */}
      <header className="bg-white shadow-sm sticky top-0 z-50 py-3 transition-all duration-300">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 lg:gap-6">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center group">
              <span className="text-2xl sm:text-3xl font-extrabold text-[#FF6600] tracking-tighter">1688Euro</span>
              <div className="ml-2 flex flex-col leading-none">
                <span className="text-[10px] sm:text-xs font-bold text-gray-900 uppercase tracking-widest">Europe</span>
                <span className="text-[8px] sm:text-[10px] text-gray-500">Global Sourcing</span>
              </div>
            </Link>

            {/* Categories Button */}
            <div className="relative hidden lg:block">
              <button
                className="flex items-center space-x-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded text-sm font-medium hover:bg-gray-100 hover:border-[#FF6600] transition-colors"
                onMouseEnter={() => setIsCategoriesOpen(true)}
              >
                <SafeIcon icon={FiMenu} className="w-4 h-4" />
                <span>{t('nav.categories')}</span>
              </button>

              {/* Categories Dropdown */}
              <AnimatePresence>
                {isCategoriesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50"
                    onMouseEnter={() => setIsCategoriesOpen(true)}
                    onMouseLeave={() => setIsCategoriesOpen(false)}
                  >
                    <div className="p-2">
                      {categories.map((category) => (
                        <Link
                          key={category.name}
                          to={category.href}
                          className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#FF6600] rounded-md transition-colors"
                          onClick={() => setIsCategoriesOpen(false)}
                        >
                          <span className="text-lg">{category.icon}</span>
                          <span>{category.name}</span>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl">
              <SearchBar />
            </div>

            {/* Right Side Actions */}
            <div className="hidden sm:flex items-center space-x-4 flex-shrink-0">
              {/* Messages */}
              <Link to="/messages" className="flex flex-col items-center cursor-pointer group text-gray-600 hover:text-[#FF6600]">
                <SafeIcon icon={FiMessageSquare} className="w-6 h-6" />
                <span className="text-xs mt-1">{t('nav.messages')}</span>
              </Link>

              {/* Orders */}
              <Link to="/orders" className="flex flex-col items-center cursor-pointer group text-gray-600 hover:text-[#FF6600]">
                <SafeIcon icon={FiPackage} className="w-6 h-6" />
                <span className="text-xs mt-1">{t('nav.orders')}</span>
              </Link>

              {/* Cart */}
              <Link to="/cart" className="flex flex-col items-center cursor-pointer group text-gray-600 hover:text-[#FF6600] relative">
                <SafeIcon icon={FiShoppingCart} className="w-6 h-6" />
                <span className="text-xs mt-1">{t('nav.cart')}</span>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-2 bg-[#FF6600] text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-bold shadow-sm">
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Navigation */}
      <div className="bg-white border-b border-gray-200 shadow-sm hidden sm:block">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-8 py-2 overflow-x-auto scrollbar-hide">
            {mainNavLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`whitespace-nowrap text-sm font-medium transition-colors border-b-2 border-transparent hover:border-[#FF6600] pb-1 ${
                  isActiveLink(link.href) ? 'text-[#FF6600] border-[#FF6600]' : 'text-gray-600 hover:text-[#FF6600]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export default Header